import { HttpClient, HttpHeaders } from '@angular/common/http';
import { DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { updateState, withDevtools } from '@angular-architects/ngrx-toolkit';
import { tapResponse } from '@ngrx/operators';
import { signalStore, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { catchError, forkJoin, map, of, switchMap } from 'rxjs';
import { BnInternalHeaders } from '@bn/shared/entities/enums';
import { getStoreEvents, StoreRequest, withDefaultRequestStatusState } from '@bn/shared/state-store';
import { CustomResponsePagesService } from '../openapi/api/custom-response-pages.service';

const events = getStoreEvents('SHIELD_CUSTOM_PAGES', ['LOAD', 'LOAD_ALL', 'SAVE', 'RESET']);

export type CustomPageType = 'challenge' | 'block' | 'ratelimit';

export interface CustomPages {
  challenge: string | null;
  block: string | null;
  ratelimit: string | null;
}

export interface ShieldCustomPagesStoreType {
  customPages: CustomPages;
}

const initialCustomPages: CustomPages = {
  challenge: null,
  block: null,
  ratelimit: null,
};

const initialState: ShieldCustomPagesStoreType = {
  customPages: initialCustomPages,
};

const PAGE_TYPES: CustomPageType[] = ['challenge', 'block', 'ratelimit'];

export const ShieldCustomPagesApiStore = signalStore(
  { providedIn: 'root' },
  withDevtools('Shield.CustomPages'),
  withState<ShieldCustomPagesStoreType>(initialState),
  withDefaultRequestStatusState(),

  withMethods((store, customPagesService = inject(CustomResponsePagesService), http = inject(HttpClient), destroyRef = inject(DestroyRef)) => ({
    loadCustomPage: rxMethod<{ shieldZoneId: number; pageType: CustomPageType }>(
      switchMap(({ shieldZoneId, pageType }) => {
        updateState(store, events.LOAD.REQUEST, store.setLoading(StoreRequest.Item));

        const basePath = customPagesService.configuration.basePath;
        const url = `${basePath}/shield/shield-zone/${shieldZoneId}/custom-page/${pageType}`;

        return http.get(url, { responseType: 'text', headers: new HttpHeaders({ [BnInternalHeaders.IgnoreErrorToast]: 'true' }) }).pipe(
          takeUntilDestroyed(destroyRef),
          tapResponse({
            next: (htmlContent: string) => {
              updateState(
                store,
                events.LOAD.SUCCESS,
                { customPages: { ...store.customPages(), [pageType]: htmlContent } },
                store.setFinished(StoreRequest.Item),
              );
            },
            error: () => {
              updateState(store, events.LOAD.ERROR, store.setFinished(StoreRequest.Item));
            },
          }),
        );
      }),
    ),

    loadAllCustomPages: rxMethod<{ shieldZoneId: number }>(
      switchMap(({ shieldZoneId }) => {
        updateState(store, events.LOAD_ALL.REQUEST, store.setLoading(StoreRequest.Item));

        const basePath = customPagesService.configuration.basePath;

        return forkJoin(
          PAGE_TYPES.map((pageType) =>
            http
              .get(`${basePath}/shield/shield-zone/${shieldZoneId}/custom-page/${pageType}`, {
                responseType: 'text',
                headers: new HttpHeaders({ [BnInternalHeaders.IgnoreErrorToast]: 'true' }),
              })
              .pipe(
                map((html) => ({ pageType, html: html as string })),
                catchError(() => of({ pageType, html: null })),
              ),
          ),
        ).pipe(
          takeUntilDestroyed(destroyRef),
          tapResponse({
            next: (results) => {
              const customPages = results.reduce((acc, { pageType, html }) => ({ ...acc, [pageType]: html }), {} as CustomPages);
              updateState(store, events.LOAD_ALL.SUCCESS, { customPages }, store.setFinished(StoreRequest.Item));
            },
            error: () => updateState(store, events.LOAD_ALL.ERROR, store.setFinished(StoreRequest.Item)),
          }),
        );
      }),
    ),

    saveCustomPage: rxMethod<{ shieldZoneId: number; pageType: CustomPageType; htmlContent: string }>(
      switchMap(({ shieldZoneId, pageType, htmlContent }) => {
        updateState(store, events.SAVE.REQUEST, store.setLoading(StoreRequest.Update));

        const basePath = customPagesService.configuration.basePath;
        const url = `${basePath}/shield/shield-zone/${shieldZoneId}/custom-page/${pageType}`;

        return http.put(url, htmlContent, { headers: new HttpHeaders({ 'Content-Type': 'text/plain' }) }).pipe(
          takeUntilDestroyed(destroyRef),
          tapResponse({
            next: () => {
              updateState(
                store,
                events.SAVE.SUCCESS,
                { customPages: { ...store.customPages(), [pageType]: htmlContent } },
                store.setFinished(StoreRequest.Update),
              );
            },
            error: ({ error }) => updateState(store, events.SAVE.ERROR, store.setError(StoreRequest.Update, error)),
          }),
        );
      }),
    ),

    resetToDefault: rxMethod<{ shieldZoneId: number; pageType: CustomPageType }>(
      switchMap(({ shieldZoneId, pageType }) => {
        updateState(store, events.RESET.REQUEST, store.setLoading(StoreRequest.Delete));

        return customPagesService.deleteCustomResponsePage({ shieldZoneId, pageType }).pipe(
          takeUntilDestroyed(destroyRef),
          tapResponse({
            next: () => {
              updateState(store, events.RESET.SUCCESS, { customPages: { ...store.customPages(), [pageType]: null } }, store.setFinished(StoreRequest.Delete));
            },
            error: ({ error }) => updateState(store, events.RESET.ERROR, store.setError(StoreRequest.Delete, error)),
          }),
        );
      }),
    ),
  })),
);
